import { useCallback, useRef, useState } from 'react';
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';

import { usePlanEditor } from '../editor';

import type { BarDragMode } from './TimelineBar';
import { DEFAULT_BLOCK_DAYS, DRAG_THRESHOLD_PX } from './timelineGeometry';
import {
  dayAtOffsetX,
  groupShiftBounds,
  offsetToIso,
  resizeOwnSpan,
  spanToIsoPatch,
} from './timelineModel';
import type { ScheduledNode, TimelineModel, TimelineSpan } from './timelineModel';

import { formatShortDate } from '@/lib';

type DateUpdate = { id: string; startDate?: string; endDate?: string; targetDate?: string };

type Session = {
  nodeId: string;
  mode: BarDragMode;
  /** Pointer x where the press started. */
  originX: number;
  /** Zoom at press time: a mid-drag zoom must not re-scale a live originX delta. */
  dayWidth: number;
  /** The VISIBLE bar at press time — what the pointer actually grabbed. */
  barSpan: TimelineSpan | null;
  /** The dragged node's own stored span (null for a pure rollup parent). */
  ownSpan: TimelineSpan | null;
  /** Everything beneath it: the floor a resize cannot shrink past. */
  descendantSpan: TimelineSpan | null;
  /** Last own span dispatched, so a drag held against the floor commits nothing. */
  lastApplied: TimelineSpan | null;
  /** The whole subtree's scheduled nodes at press time — the group payload. */
  payload: readonly ScheduledNode[];
  bounds: { minDelta: number; maxDelta: number };
  /** Last committed day delta, so we dispatch once per crossed column. */
  lastDelta: number;
  moved: boolean;
};

/**
 * What the dates will be if the pointer is released now.
 *
 * A bar drag used to have no feedback at all: the only date text in the view is
 * the bar's native `title`, which needs the pointer to stop and hover. A pilot
 * PM dragged bars without ever connecting them to the dates in the side panel,
 * and learnt what she had done only after letting go.
 */
export type BarDragReadout = {
  /** `02 Jun → 15 Aug · 12 d` — the span as it stands mid-drag. */
  text: string;
  /** Why the bar stopped following the pointer, when it has. */
  note?: string;
};

export type BarDragApi = {
  /** The node currently being dragged, for the grabbing cursor and shadow. */
  draggingId: string | null;
  /** Dates as they stand mid-drag; null when no drag is in flight. */
  readout: BarDragReadout | null;
  /** The readout card, positioned imperatively at pointer rate. */
  cardRef: RefObject<HTMLDivElement | null>;
  /** Handler factory for a row's bar; `mode` picks move vs either edge. */
  barPointerDown: (
    nodeId: string,
  ) => (mode: BarDragMode) => (event: ReactPointerEvent<HTMLElement>) => void;
  /** Click on an empty track: schedule a default block at the clicked day. */
  trackClick: (nodeId: string) => (event: ReactMouseEvent<HTMLElement>) => void;
};

/**
 * Pointer handling for the timeline's three edits.
 *
 * Everything mutable lives in a ref: a mousemove does integer arithmetic and, at
 * most once per crossed 7px column, one dispatch. Nothing here walks the tree —
 * the payload and its clamp bounds are read out of the model once, on pointerdown.
 *
 * Because each dispatch bumps `revision`, the 800ms autosave timer re-arms on
 * every one: a continuous drag produces exactly one PUT once the pointer settles.
 */
export const useBarDrag = (model: TimelineModel, dayWidth: number): BarDragApi => {
  const { t } = useTranslation('pmPlanning');
  const { state, dispatch, flat } = usePlanEditor();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [readout, setReadout] = useState<BarDragReadout | null>(null);
  const session = useRef<Session | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  /**
   * The span as words. Set at the same cadence as the dispatch — once per
   * crossed column — so it costs no render the drag was not already paying for.
   */
  const describeSpan = useCallback(
    (span: TimelineSpan, note?: string): BarDragReadout => {
      const days = span.endOffset - span.startOffset + 1;
      const text = `${formatShortDate(offsetToIso(model.window, span.startOffset))} → ${formatShortDate(
        offsetToIso(model.window, span.endOffset),
      )} · ${t('timeline.DaysShort', { count: days })}`;
      return note === undefined ? { text } : { text, note };
    },
    [model.window, t],
  );

  const applyDelta = useCallback(
    (current: Session, delta: number) => {
      const window = model.window;

      if (current.mode === 'move') {
        // Whole subtree: every node with dates of its own shifts by the same
        // delta, so relative offsets inside the group are preserved exactly.
        const updates: DateUpdate[] = current.payload.map((node) => {
          const shifted = {
            startOffset: node.startOffset + delta,
            endOffset: node.endOffset + delta,
          };
          return { id: node.id, ...spanToIsoPatch(window, shifted) };
        });
        if (updates.length > 0) dispatch({ type: 'SET_NODE_DATES', updates });
        if (current.barSpan) {
          setReadout(
            describeSpan({
              startOffset: current.barSpan.startOffset + delta,
              endOffset: current.barSpan.endOffset + delta,
            }),
          );
        }
        return;
      }

      /*
        Resize writes only the node itself. Ancestors need no patch: a parent's
        bar is the union of its own dates and its descendants', so it re-derives
        on the next render. Refreshing a parent's STORED dates here would be
        actively wrong — they are its own scheduled window, which is legitimately
        wider than its children (Project Ship's "Backend implementation" runs
        three weeks longer than the subtask inside it).

        A parent resizes too (ADR 0020), floored by its descendants; resizeOwnSpan
        seeds from the visible bar but writes back only the dragged edge.
      */
      if (!current.barSpan) return;
      const nextOwn = resizeOwnSpan(
        current.barSpan,
        current.ownSpan,
        current.descendantSpan,
        current.mode,
        delta,
        window.dayCount,
      );
      /*
        Pushing against the floor must not dirty the document: updateManyNodeFields
        spreads any non-empty patch into a fresh object, so withPlan would bump the
        revision and queue a PUT that changes nothing.
      */
      if (
        current.lastApplied &&
        nextOwn.startOffset === current.lastApplied.startOffset &&
        nextOwn.endOffset === current.lastApplied.endOffset
      ) {
        // The edge has stopped following the pointer. Say why when there is a
        // reason to give — a bar that simply refuses to move is the thing that
        // reads as a broken drag.
        setReadout(
          describeSpan(nextOwn, current.descendantSpan ? t('timeline.EdgeHeld') : undefined),
        );
        return;
      }
      current.lastApplied = nextOwn;
      setReadout(describeSpan(nextOwn));
      dispatch({
        type: 'SET_NODE_DATES',
        updates: [{ id: current.nodeId, ...spanToIsoPatch(window, nextOwn) }],
      });
    },
    [describeSpan, dispatch, model.window, t],
  );

  const barPointerDown = useCallback(
    (nodeId: string) => (mode: BarDragMode) => (event: ReactPointerEvent<HTMLElement>) => {
      // A resize handle sits inside the bar, so the bar's own 'move' handler
      // would otherwise fire too.
      event.stopPropagation();
      event.preventDefault();

      const barSpan = model.spanById.get(nodeId) ?? null;
      const ownSpan = model.ownSpanById.get(nodeId) ?? null;
      // Only a group move needs the subtree payload; copying the slice for a
      // resize would allocate the whole subtree for nothing.
      const slice = mode === 'move' ? model.sliceById.get(nodeId) : undefined;
      const payload = slice ? model.scheduledNodes.slice(slice.from, slice.to) : [];
      // Nothing scheduled in this subtree — a rollup with no dates anywhere.
      if (mode === 'move' && payload.length === 0) return;
      // A handle cannot exist without a bar, but keep the branch total.
      if (mode !== 'move' && !barSpan) return;

      session.current = {
        nodeId,
        mode,
        originX: event.clientX,
        dayWidth,
        barSpan,
        ownSpan,
        descendantSpan: model.descendantSpanById.get(nodeId) ?? null,
        lastApplied: ownSpan,
        payload,
        bounds: groupShiftBounds(payload, model.window.dayCount),
        lastDelta: 0,
        moved: false,
      };

      const element = event.currentTarget;
      element.setPointerCapture(event.pointerId);

      const onMove = (moveEvent: PointerEvent) => {
        const current = session.current;
        if (!current) return;
        const dx = moveEvent.clientX - current.originX;

        if (!current.moved) {
          // Below the threshold this is still a click, not a drag.
          if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
          current.moved = true;
          document.body.style.userSelect = 'none';
          setDraggingId(current.nodeId);
          // Seed the readout with where the bar stands, so it is populated the
          // instant the card appears rather than after the first column.
          if (current.barSpan) setReadout(describeSpan(current.barSpan));
        }

        // The card follows imperatively — a React render per move would be
        // wasted work at pointer rate. Same geometry as the restructure card.
        const card = cardRef.current;
        if (card) {
          const flipX = moveEvent.clientX > globalThis.innerWidth - 360;
          const flipY = moveEvent.clientY > globalThis.innerHeight - 120;
          card.style.transform = `translate3d(${String(
            moveEvent.clientX + (flipX ? -340 : 14),
          )}px, ${String(moveEvent.clientY + (flipY ? -110 : 18))}px, 0)`;
        }

        let delta = Math.round(dx / current.dayWidth);
        if (current.mode === 'move') {
          delta = Math.max(current.bounds.minDelta, Math.min(current.bounds.maxDelta, delta));
        }
        if (delta === current.lastDelta) return;
        current.lastDelta = delta;
        applyDelta(current, delta);
      };

      const onUp = () => {
        element.removeEventListener('pointermove', onMove);
        element.removeEventListener('pointerup', onUp);
        element.removeEventListener('pointercancel', onUp);
        document.body.style.userSelect = '';
        const current = session.current;
        session.current = null;
        setDraggingId(null);
        setReadout(null);
        // A press that never crossed the threshold selects the row instead.
        if (current && !current.moved && state.activeId !== current.nodeId) {
          dispatch({ type: 'SET_ACTIVE', id: current.nodeId });
        }
      };

      element.addEventListener('pointermove', onMove);
      element.addEventListener('pointerup', onUp);
      element.addEventListener('pointercancel', onUp);
    },
    [applyDelta, dayWidth, describeSpan, dispatch, model, state.activeId],
  );

  const trackClick = useCallback(
    (nodeId: string) => (event: ReactMouseEvent<HTMLElement>) => {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const window = model.window;
      // Clamp so the whole default block fits inside the window.
      const startOffset = dayAtOffsetX(
        event.clientX - rect.left,
        dayWidth,
        window.dayCount,
        window.dayCount - DEFAULT_BLOCK_DAYS,
      );
      const endOffset = startOffset + DEFAULT_BLOCK_DAYS - 1;

      const update: DateUpdate = {
        id: nodeId,
        startDate: offsetToIso(window, startOffset),
        endDate: offsetToIso(window, endOffset),
      };
      // Give it a target date too when it has none, so the CALC-6 "days left"
      // column stops reading "No target" the moment the task is scheduled.
      if (!flat.index.nodeById.get(nodeId)?.targetDate) {
        update.targetDate = offsetToIso(window, endOffset);
      }

      dispatch({ type: 'SET_NODE_DATES', updates: [update] });
      dispatch({ type: 'SET_ACTIVE', id: nodeId });
    },
    [dayWidth, dispatch, flat.index, model.window],
  );

  return { draggingId, readout, cardRef, barPointerDown, trackClick };
};
